import { SQUARES, type Square } from "chess.js";

export const PRINCIPLE_IDS = [
  "center_claim",
  "pieces_out",
  "tempo_discipline",
  "queen_patience",
  "pawn_economy",
  "king_shelter",
  "center_lock",
  "rook_harmony",
  "threat_read",
  "force_first",
  "nothing_loose",
  "worst_piece",
  "file_control",
  "outpost",
  "break_timing",
  "pawn_integrity",
  "clean_conversion",
  "active_king",
  "opposition",
  "passer_support",
] as const;

export type PrincipleId = (typeof PRINCIPLE_IDS)[number];
export type PrincipleStatus = "applied" | "missed";
export type PrinciplePhase = "opening" | "middlegame" | "endgame";
export type PrincipleAnchorRole = "subject" | "support" | "target" | "danger" | "key_square";
export type PrincipleArrowRole = "played" | "verified_alternative" | "threat" | "support";

export interface PrincipleAnchor {
  square: Square;
  role: PrincipleAnchorRole;
}
export interface PrincipleArrow {
  from: Square;
  to: Square;
  role: PrincipleArrowRole;
}
export interface PrincipleSignal {
  principle: PrincipleId;
  status: PrincipleStatus;
  phase: PrinciplePhase;
  detectorVersion: 1;
  anchors: readonly PrincipleAnchor[];
  arrows: readonly PrincipleArrow[];
  alternativeSan: string | null;
  proofSans: readonly string[];
  evaluationImpactCp: number | null;
  metrics: Record<string, number | string | boolean>;
}

export interface PrinciplePresentation {
  name: string;
  traditionalName: string;
  cue: string;
}

const CATALOG: Record<PrincipleId, PrinciplePresentation> = {
  center_claim: {
    name: "CENTER CLAIM",
    traditionalName: "Control the center",
    cue: "Contest the center before drifting to the wing.",
  },
  pieces_out: {
    name: "PIECES OUT",
    traditionalName: "Develop the minor pieces",
    cue: "Bring new pieces into the game.",
  },
  tempo_discipline: {
    name: "TEMPO DISCIPLINE",
    traditionalName: "Avoid repeated opening moves",
    cue: "Avoid spending another tempo on the same piece without a concrete reason.",
  },
  queen_patience: {
    name: "QUEEN PATIENCE",
    traditionalName: "Delay early queen development",
    cue: "Develop the army before exposing the queen.",
  },
  pawn_economy: {
    name: "PAWN ECONOMY",
    traditionalName: "Avoid unnecessary pawn moves",
    cue: "Make pawn moves serve development, space, or a concrete break.",
  },
  king_shelter: {
    name: "KING SHELTER",
    traditionalName: "Secure the king",
    cue: "Secure the king before opening more fronts.",
  },
  center_lock: {
    name: "CENTER LOCK",
    traditionalName: "Preserve central tension",
    cue: "Resolve central tension only when the resulting structure helps you.",
  },
  rook_harmony: { name: "ROOK HARMONY", traditionalName: "Connect the rooks", cue: "Connect and activate the rooks." },
  threat_read: {
    name: "THREAT READ",
    traditionalName: "Identify the opponent's threat",
    cue: "Name the opponent's immediate threat before choosing your move.",
  },
  force_first: {
    name: "FORCE FIRST",
    traditionalName: "Checks, captures, and threats",
    cue: "Check forcing moves before quiet improvements.",
  },
  nothing_loose: {
    name: "NOTHING LOOSE",
    traditionalName: "Loose pieces drop off",
    cue: "Account for every undefended piece.",
  },
  worst_piece: {
    name: "WORST PIECE",
    traditionalName: "Improve the worst-placed piece",
    cue: "Improve the piece contributing least.",
  },
  file_control: {
    name: "FILE CONTROL",
    traditionalName: "Occupy open and half-open files",
    cue: "Use open and half-open files before they disappear.",
  },
  outpost: {
    name: "OUTPOST",
    traditionalName: "Establish a protected outpost",
    cue: "Occupy stable squares the opponent cannot challenge with a pawn.",
  },
  break_timing: {
    name: "BREAK TIMING",
    traditionalName: "Prepare pawn breaks",
    cue: "Prepare the pawn break before releasing the tension.",
  },
  pawn_integrity: {
    name: "PAWN INTEGRITY",
    traditionalName: "Preserve the pawn structure",
    cue: "Avoid permanent pawn weaknesses without concrete compensation.",
  },
  clean_conversion: {
    name: "CLEAN CONVERSION",
    traditionalName: "Reduce counterplay when ahead",
    cue: "When ahead, reduce counterplay before collecting more material.",
  },
  active_king: {
    name: "ACTIVE KING",
    traditionalName: "Activate the endgame king",
    cue: "Activate the king once major-piece danger is gone.",
  },
  opposition: {
    name: "OPPOSITION",
    traditionalName: "Take the opposition",
    cue: "Fight for the key king squares before pushing.",
  },
  passer_support: {
    name: "PASSER SUPPORT",
    traditionalName: "Support the passed pawn",
    cue: "Support the passed pawn before advancing it.",
  },
};

const IDs = new Set<string>(PRINCIPLE_IDS);
const SQUARE_SET = new Set<string>(SQUARES);
const STATUSES = new Set(["applied", "missed"]);
const PHASES = new Set(["opening", "middlegame", "endgame"]);
const ANCHOR_ROLES = new Set(["subject", "support", "target", "danger", "key_square"]);
const ARROW_ROLES = new Set(["played", "verified_alternative", "threat", "support"]);
const SAN = /^(?:O-O(?:-O)?|[KQRBN]?[a-h]?[1-8]?x?[a-h][1-8](?:=[QRBN])?)[+#]?$/;
const KEYS = new Set([
  "principle",
  "status",
  "phase",
  "detectorVersion",
  "anchors",
  "arrows",
  "alternativeSan",
  "proofSans",
  "evaluationImpactCp",
  "metrics",
]);
const record = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === "object" && !Array.isArray(value);
const exactKeys = (value: Record<string, unknown>, keys: Set<string>) =>
  Object.keys(value).every((key) => keys.has(key));
const san = (value: unknown): value is string => typeof value === "string" && value.length <= 32 && SAN.test(value);

export const principlePresentation = (principle: PrincipleId): PrinciplePresentation => CATALOG[principle];

export const parsePrincipleSignals = (value: unknown): PrincipleSignal[] | null => {
  if (!Array.isArray(value) || value.length > 20) return null;
  const result: PrincipleSignal[] = [];
  for (const item of value) {
    if (
      !record(item) ||
      !exactKeys(item, KEYS) ||
      !IDs.has(String(item.principle)) ||
      !STATUSES.has(String(item.status)) ||
      !PHASES.has(String(item.phase)) ||
      item.detectorVersion !== 1
    )
      return null;
    if (
      !Array.isArray(item.anchors) ||
      item.anchors.length < 1 ||
      item.anchors.length > 8 ||
      item.anchors.some(
        (anchor) =>
          !record(anchor) ||
          Object.keys(anchor).some((key) => key !== "square" && key !== "role") ||
          !SQUARE_SET.has(String(anchor.square)) ||
          !ANCHOR_ROLES.has(String(anchor.role)),
      )
    )
      return null;
    if (
      !Array.isArray(item.arrows) ||
      item.arrows.length > 8 ||
      item.arrows.some(
        (arrow) =>
          !record(arrow) ||
          Object.keys(arrow).some((key) => !["from", "to", "role"].includes(key)) ||
          !SQUARE_SET.has(String(arrow.from)) ||
          !SQUARE_SET.has(String(arrow.to)) ||
          !ARROW_ROLES.has(String(arrow.role)),
      )
    )
      return null;
    if (item.alternativeSan !== null && !san(item.alternativeSan)) return null;
    if (!Array.isArray(item.proofSans) || item.proofSans.length > 8 || item.proofSans.some((move) => !san(move)))
      return null;
    if (
      item.evaluationImpactCp !== null &&
      (typeof item.evaluationImpactCp !== "number" || !Number.isFinite(item.evaluationImpactCp))
    )
      return null;
    if (
      !record(item.metrics) ||
      Object.keys(item.metrics).length > 12 ||
      Object.entries(item.metrics).some(
        ([key, metric]) =>
          !/^[a-z][A-Za-z0-9]{0,39}$/.test(key) ||
          !(["number", "string", "boolean"] as string[]).includes(typeof metric) ||
          (typeof metric === "number" && !Number.isFinite(metric)) ||
          (typeof metric === "string" && metric.length > 120),
      )
    )
      return null;
    result.push(item as unknown as PrincipleSignal);
  }
  return result;
};
